import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ── Helpers ───────────────────────────────────────────────────────────────────

function Section({ id, title, badge, description, children }) {
  return (
    <div id={id} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4 scroll-mt-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          {badge && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              {badge}
            </span>
          )}
        </div>
        {description && (
          <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function Label({ children, optional }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {children}
      {optional && <span className="ml-1 text-xs font-normal text-gray-600">(optional)</span>}
    </label>
  );
}

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-gray-500 focus:border-transparent';

function TagField({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');

  const add = () => {
    const t = input.trim();
    if (!t || values.includes(t)) { setInput(''); return; }
    onChange([...values, t]);
    setInput('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-200 rounded-full text-xs font-medium text-teal-800">
            {v}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))}
              className="text-teal-400 hover:text-teal-700 leading-none ml-0.5">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className={inp}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <button type="button" onClick={add}
          className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 whitespace-nowrap">
          Add
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group">
      <div className="relative mt-0.5 shrink-0">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
        <div className={`w-10 h-6 rounded-full transition-colors ${checked ? 'bg-teal-600' : 'bg-gray-200'}`} />
        <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {description && <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </label>
  );
}

const DEFAULT_TRIP_TYPES = [
  {
    id: 'STX-Managed',
    label: 'STX-Managed Travel',
    description: 'STX arranges all bookings (flights, hotels, transfers) on your behalf. Staff submit a travel request and STX handles everything.',
  },
  {
    id: 'Self-Managed',
    label: 'Self-Managed Travel',
    description: 'Staff arrange their own bookings and log the details in the portal for record-keeping, reimbursement, and reporting.',
  },
  {
    id: 'Group Event',
    label: 'Group Events',
    description: 'Group travel for conferences, team events, or multi-person trips. Supports multiple passengers on a single trip record.',
  },
];

const DEFAULT_SECTOR_TYPES = [
  { id: 'Flight',        description: 'Airfares and any airport-related costs.' },
  { id: 'Accommodation', description: 'Hotels, motels, serviced apartments, and other overnight stays.' },
  { id: 'Car Hire',      description: 'Rental vehicles used during the trip.' },
  { id: 'Parking',       description: 'Airport, hotel, or venue parking fees.' },
  { id: 'Transfers',     description: 'Taxis, rideshare, shuttle, or other ground transfers.' },
  { id: 'Meals',         description: 'Meal allowances and food expenses.' },
  { id: 'Other',         description: 'Any other travel-related cost not covered above.' },
];

const FEATURES = [
  {
    key: 'hotelBooking',
    label: 'Hotel booking through portal',
    description: 'Search and book hotels directly through the STX portal using preferred rates. Useful for STX-Managed trips where STX books accommodation on your behalf.',
    defaultOn: true,
  },
  {
    key: 'reports',
    label: 'Reports & analytics',
    description: 'Access spend dashboards and reports: destination analysis, cost centre breakdowns, accommodation policy compliance, and more.',
    defaultOn: true,
  },
  {
    key: 'fileAttachments',
    label: 'File attachments',
    description: 'Attach documents to trips — such as pre-approval forms, quotes, or receipts.',
    defaultOn: true,
  },
  {
    key: 'accessibilityToolbar',
    label: 'Accessibility toolbar',
    description: 'Provides visual accessibility tools for portal users: text size, contrast modes, dyslexia-friendly fonts, and keyboard navigation.',
    defaultOn: true,
  },
  {
    key: 'accommodationPolicy',
    label: 'Accommodation policy reporting',
    description: 'Set maximum nightly accommodation rates per city and see compliance tracked in the Travel Policy report.',
    defaultOn: false,
  },
  {
    key: 'flightPolicy',
    label: 'Flight cost policy reporting',
    description: 'Set maximum per-trip flight costs by destination and track compliance in the Travel Policy report.',
    defaultOn: false,
  },
  {
    key: 'carbonTracking',
    label: 'Carbon footprint tracking',
    description: 'Track estimated CO₂ emissions for flights using DEFRA 2023 factors. Results appear in the Carbon Footprint report.',
    defaultOn: false,
  },
  {
    key: 'invoiceGeneration',
    label: 'Invoice generation',
    description: 'Allow STX to generate itemised invoices for your travel spend directly through the portal, with line items per trip and fee.',
    defaultOn: true,
  },
  {
    key: 'customPermissions',
    label: 'Custom user permissions',
    description: 'Allow your admin users to grant or restrict individual staff permissions beyond their default role — useful for mixed-role staff.',
    defaultOn: false,
  },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function OnboardingForm() {
  const { token } = useParams();

  const [loading, setLoading]   = useState(true);
  const [docData, setDocData]   = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');

  // Form state
  const [portalTitle, setPortalTitle]           = useState('');
  const [logo, setLogo]                         = useState('');
  const [primaryColor, setPrimaryColor]         = useState('#1e40af');
  const [secondaryColor, setSecondaryColor]     = useState('#93c5fd');
  const [costCentres, setCostCentres]           = useState([]);
  const [enabledTripTypes, setEnabledTripTypes] = useState(['STX-Managed', 'Self-Managed', 'Group Event']);
  const [customTripTypes, setCustomTripTypes]   = useState([]);
  const [approvalByType, setApprovalByType]     = useState({});
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [features, setFeatures]                 = useState(() =>
    Object.fromEntries(FEATURES.map(f => [f.key, f.defaultOn]))
  );
  const [sectorTypes, setSectorTypes]                         = useState(DEFAULT_SECTOR_TYPES.map(s => s.id));
  const [customSectorTypes, setCustomSectorTypes]             = useState([]);
  const [selfManagedHotelBooking, setSelfManagedHotelBooking] = useState(true);
  const [gstRate, setGstRate]                   = useState(0.10);
  const [accomRates, setAccomRates]             = useState([]);
  const [newAccomCity, setNewAccomCity]         = useState('');
  const [newAccomRate, setNewAccomRate]         = useState('');
  const [flightRates, setFlightRates]           = useState([]);
  const [newFlightDest, setNewFlightDest]       = useState('');
  const [newFlightRate, setNewFlightRate]       = useState('');
  const [variance, setVariance]                 = useState({
    accommodation: { enabled: false, type: 'percent', value: 10, action: 'warn' },
    flight:        { enabled: false, type: 'percent', value: 10, action: 'warn' },
  });
  const [mfaEnabled, setMfaEnabled]             = useState(false);
  const [mfaMode, setMfaMode]                   = useState('optional');
  const [budgetEnabled, setBudgetEnabled]       = useState(false);
  const [budgetFY, setBudgetFY]                 = useState(() =>
    new Date().getMonth() >= 6 ? new Date().getFullYear() + 1 : new Date().getFullYear()
  );
  const [budgetOverall, setBudgetOverall]       = useState('');
  const [budgetThreshold, setBudgetThreshold]   = useState(80);
  const [budgetByCC, setBudgetByCC]             = useState({});
  const [notes, setNotes]                       = useState('');

  useEffect(() => {
    getDoc(doc(db, 'onboarding', token)).then(snap => {
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      const d = snap.data();
      if (d.status === 'submitted' || d.status === 'applied') { setSubmitted(true); setLoading(false); return; }
      if (d.expiresAt && new Date(d.expiresAt) < new Date()) { setExpired(true); setLoading(false); return; }
      setDocData(d);
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [token]);

  const allTripTypes = [...enabledTripTypes, ...customTripTypes];

  const toggleTripType = (id) => {
    setEnabledTripTypes(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const addAccomRate = () => {
    const city = newAccomCity.trim();
    const rate = parseFloat(newAccomRate);
    if (!city || isNaN(rate) || rate <= 0) return;
    if (accomRates.find(r => r.city === city)) return;
    setAccomRates(prev => [...prev, { city, rate }]);
    setNewAccomCity('');
    setNewAccomRate('');
  };

  const removeAccomRate = (city) => setAccomRates(prev => prev.filter(r => r.city !== city));

  const addFlightRate = () => {
    const dest = newFlightDest.trim();
    const rate = parseFloat(newFlightRate);
    if (!dest || isNaN(rate) || rate <= 0) return;
    if (flightRates.find(r => r.city === dest)) return;
    setFlightRates(prev => [...prev, { city: dest, rate }]);
    setNewFlightDest(''); setNewFlightRate('');
  };
  const removeFlightRate = (city) => setFlightRates(prev => prev.filter(r => r.city !== city));

  const setVar = (type, key, value) =>
    setVariance(prev => ({ ...prev, [type]: { ...prev[type], [key]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const accomRatesObj  = Object.fromEntries(accomRates.map(r => [r.city, r.rate]));
      const flightRatesObj = Object.fromEntries(flightRates.map(r => [r.city, r.rate]));
      await updateDoc(doc(db, 'onboarding', token), {
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        responses: {
          portalTitle,
          logo,
          primaryColor,
          secondaryColor,
          costCentres,
          tripTypes: allTripTypes,
          sectorTypes: [...sectorTypes, ...customSectorTypes],
          approvalByTripType: approvalByType,
          emailNotifications,
          features,
          selfManagedHotelBooking,
          mfa: { enabled: mfaEnabled, mode: mfaMode },
          budgets: budgetEnabled && (parseFloat(budgetOverall) > 0 || Object.values(budgetByCC).some(v => v > 0))
            ? [{ fiscalYear: budgetFY, overall: parseFloat(budgetOverall) || 0, alertThreshold: budgetThreshold, byCostCentre: budgetByCC }]
            : [],
          gstRate,
          accomRates: accomRatesObj,
          flightRates: flightRatesObj,
          policyVariance: variance,
          notes,
        },
      });
      setDone(true);
    } catch (err) {
      setError('Something went wrong. Please try again or contact STX.');
      console.error(err);
    }
    setSaving(false);
  };

  // ── Render states ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600 text-sm">Loading…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-gray-700 font-medium mb-2">Form not found</p>
          <p className="text-sm text-gray-600">This link may be invalid. Please contact STX Corporate for assistance.</p>
        </div>
      </Shell>
    );
  }

  if (expired) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-gray-700 font-medium mb-2">This link has expired</p>
          <p className="text-sm text-gray-600">Please contact STX Corporate to receive a new onboarding link.</p>
        </div>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <div className="text-center py-12">
          <div className="text-4xl mb-4">✓</div>
          <p className="text-gray-700 font-medium mb-2">Your preferences have been submitted</p>
          <p className="text-sm text-gray-700">The STX team will review your responses and be in touch to finalise your portal setup.</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell clientName={docData?.clientName}>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-800 font-semibold text-lg mb-2">Thank you, we've received your preferences!</p>
          <p className="text-sm text-gray-700 max-w-md mx-auto leading-relaxed">
            The STX Corporate team will review your responses and be in touch shortly to finalise your portal setup.
            If you have any questions in the meantime, don't hesitate to reach out.
          </p>
        </div>
      </Shell>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────

  return (
    <Shell clientName={docData?.clientName}>
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Intro */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-teal-900 mb-2">
            Welcome{docData?.recipientName ? `, ${docData.recipientName.split(' ')[0]}` : ''}!
          </h2>
          <p className="text-sm text-teal-800 leading-relaxed mb-3">
            This form helps us configure your STX Corporate Travel Portal. Work through each section
            and fill in what you can — there are no wrong answers. <strong>Anything you're unsure
            about can be left blank</strong> and we'll discuss it with you when we meet to finalise
            your setup.
          </p>
          {docData?.note && (
            <div className="mt-3 pt-3 border-t border-teal-200">
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">Note from STX</p>
              <p className="text-sm text-teal-800 italic">"{docData.note}"</p>
            </div>
          )}
        </div>

        {/* 1 — Portal identity */}
        <Section
          id="branding"
          title="1. Portal Identity"
          badge="optional"
          description="Customise how your portal looks. This is entirely optional — we can discuss branding when we meet."
        >
          <InfoBox>
            The portal title appears in the top bar of the portal. We'll show your organisation's name here.
            Colours are used for buttons and highlights throughout the interface.
          </InfoBox>

          <div>
            <Label optional>Portal title</Label>
            <input
              className={inp}
              value={portalTitle}
              onChange={e => setPortalTitle(e.target.value)}
              placeholder={docData?.clientName ? `e.g. ${docData.clientName} Travel Portal` : 'e.g. Acme Corp Travel Portal'}
            />
          </div>

          <div>
            <Label optional>Logo URL</Label>
            <input
              className={inp}
              value={logo}
              onChange={e => setLogo(e.target.value)}
              placeholder="https://yourwebsite.com/logo.png"
            />
            <p className="text-xs text-gray-600 mt-1">A direct link to your logo image (PNG or SVG). We can also set this up for you later.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label optional>Primary colour</Label>
              <div className="flex items-center gap-2">
                {/* Two controls for one value, under a single visible label that names neither of
                    them to a screen reader. Each needs its own name, and they have to differ or
                    the swatch and the hex field are indistinguishable. */}
                <input type="color" aria-label="Primary colour"
                  value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer p-0.5" />
                <input className={inp} aria-label="Primary colour hex code"
                  value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                  placeholder="#1e40af" />
              </div>
            </div>
            <div>
              <Label optional>Secondary colour</Label>
              <div className="flex items-center gap-2">
                <input type="color" aria-label="Secondary colour"
                  value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer p-0.5" />
                <input className={inp} aria-label="Secondary colour hex code"
                  value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                  placeholder="#93c5fd" />
              </div>
            </div>
          </div>
        </Section>

        {/* 2 — Cost centres */}
        <Section
          id="costcentres"
          title="2. Cost Centres"
          badge="optional"
          description="Cost centres are used to categorise travel bookings and appear in reports and invoices. These might be department names, project codes, funding streams, or budget lines."
        >
          <InfoBox>
            Examples: <em>NDIS Support, Administration, Programs, Capital Works, Board Travel, Marketing</em>.
            Type each one and press Add (or Enter). You can add more anytime through the portal.
          </InfoBox>
          <TagField values={costCentres} onChange={setCostCentres} placeholder="e.g. NDIS Support" />
        </Section>

        {/* 3 — Trip types */}
        <Section
          id="triptypes"
          title="3. Types of Travel"
          description="Select the travel types your organisation uses. You can enable all of them — they won't appear unless staff actually use them."
        >
          <InfoBox>
            These determine how a booking is processed and tracked in the portal.
          </InfoBox>

          <div className="space-y-3">
            {DEFAULT_TRIP_TYPES.map(type => (
              <label key={type.id} className="flex gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-teal-300 transition-colors"
                style={enabledTripTypes.includes(type.id) ? { borderColor: '#0d9488', background: '#f0fdfa' } : {}}>
                <input
                  type="checkbox"
                  checked={enabledTripTypes.includes(type.id)}
                  onChange={() => toggleTripType(type.id)}
                  className="mt-1 w-4 h-4 accent-teal-600 shrink-0 cursor-pointer"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">{type.label}</p>
                  <p className="text-xs text-gray-700 mt-0.5 leading-relaxed">{type.description}</p>
                </div>
              </label>
            ))}
          </div>

          <div>
            <Label optional>Other travel types</Label>
            <p className="text-xs text-gray-600 mb-2">If you use other categories of travel not listed above, add them here.</p>
            <TagField values={customTripTypes} onChange={setCustomTripTypes} placeholder="e.g. Secondments, International Conferences" />
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-0.5">Booking components</p>
              <p className="text-xs text-gray-700 leading-relaxed">
                These are the individual cost items that can be recorded within a trip — for example,
                a single trip might include a flight, accommodation, and a car hire. Untick any that
                your organisation never uses.
              </p>
            </div>
            <div className="space-y-2">
              {DEFAULT_SECTOR_TYPES.map(s => (
                <label key={s.id} className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  style={sectorTypes.includes(s.id) ? { background: '#f9fafb' } : { opacity: 0.6 }}>
                  <input
                    type="checkbox"
                    checked={sectorTypes.includes(s.id)}
                    onChange={e => setSectorTypes(prev =>
                      e.target.checked ? [...prev, s.id] : prev.filter(x => x !== s.id)
                    )}
                    className="mt-0.5 w-4 h-4 accent-teal-600 shrink-0 cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{s.id}</p>
                    <p className="text-xs text-gray-700 mt-0.5">{s.description}</p>
                  </div>
                </label>
              ))}
            </div>
            <div>
              <Label optional>Additional components</Label>
              <p className="text-xs text-gray-600 mb-2">Add any custom cost categories your organisation tracks.</p>
              <TagField values={customSectorTypes} onChange={setCustomSectorTypes} placeholder="e.g. Conference Fees, Equipment Hire" />
            </div>
          </div>
        </Section>

        {/* 4 — Approval workflow */}
        <Section
          id="approval"
          title="4. Approval Workflow"
          badge="optional"
          description="You can require travel requests to be approved by a manager or designated approver before STX proceeds with bookings."
        >
          <InfoBox>
            When approval is required for a trip type, a designated approver in your organisation
            must sign off before STX can proceed. This is useful for cost control, NDIS plan
            governance, or simply keeping managers in the loop. If unsure, leave as-is and discuss with STX.
          </InfoBox>

          <div className="space-y-3">
            {allTripTypes.map(type => (
              <Toggle
                key={type}
                checked={approvalByType[type] !== false}
                onChange={v => setApprovalByType(prev => ({ ...prev, [type]: v }))}
                label={`${type} — requires approval before booking`}
                description={
                  (approvalByType[type] !== false)
                    ? type === 'Self-Managed'
                      ? 'An approver must sign off before staff can book their own trip.'
                      : 'An approver must sign off before STX arranges this trip.'
                    : type === 'Self-Managed'
                      ? 'Self-managed trips go straight to booking without an approval step.'
                      : 'Trips of this type go straight to STX without an approval step.'
                }
              />
            ))}
          </div>
        </Section>

        {/* 5 — Email notifications */}
        <Section
          id="notifications"
          title="5. Email Notifications"
          description="Control whether the portal sends automatic emails to staff when trip status changes."
        >
          <InfoBox>
            When enabled, travellers and approvers receive emails at key stages: when a trip is submitted
            for approval, approved, declined, or confirmed by STX. They also receive a reminder 3 days
            before their trip. Staff can manage their own email preferences from the portal.
          </InfoBox>

          <Toggle
            checked={emailNotifications}
            onChange={setEmailNotifications}
            label="Send email notifications for trip status changes"
            description={emailNotifications
              ? 'Staff will receive emails at key trip stages.'
              : 'No automatic trip status emails will be sent.'}
          />
        </Section>

        {/* 6 — Features */}
        <Section
          id="features"
          title="6. Portal Features"
          description="Select which features you'd like available in your portal. You can change these at any time — STX can turn features on or off for you."
        >
          <div className="space-y-4">
            {FEATURES.map(f => (
              <React.Fragment key={f.key}>
                <Toggle
                  checked={features[f.key] ?? f.defaultOn}
                  onChange={v => setFeatures(prev => ({ ...prev, [f.key]: v }))}
                  label={f.label}
                  description={f.description}
                />
                {f.key === 'hotelBooking' && (features.hotelBooking ?? true) && (
                  <div className="ml-7 pl-4 border-l-2 border-teal-100">
                    <Toggle
                      checked={selfManagedHotelBooking}
                      onChange={setSelfManagedHotelBooking}
                      label="Allow hotel booking for Self-Managed trips"
                      description={selfManagedHotelBooking
                        ? 'Travellers on Self-Managed trips can search and book hotels directly through the portal.'
                        : 'Hotel booking is restricted to STX-Managed trips only. Self-managed travellers cannot use the hotel search.'}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Section>

        {/* 7 — Security (2FA) */}
        <Section
          id="security"
          title="7. Security — Two-Factor Authentication"
          badge="optional"
          description="Two-factor authentication (2FA) adds an extra layer of security by requiring staff to confirm their identity using an authenticator app (such as Google Authenticator or Authy) when signing in."
        >
          <InfoBox>
            With 2FA enabled, staff will need to scan a QR code in an authenticator app once during setup.
            After that, they enter a 6-digit code each time they sign in. <strong>No SMS or phone number required.</strong>
          </InfoBox>

          <Toggle
            checked={mfaEnabled}
            onChange={setMfaEnabled}
            label="Enable two-factor authentication"
            description={mfaEnabled ? '2FA will be available for staff in your portal.' : '2FA will not be offered to staff.'}
          />

          {mfaEnabled && (
            <div className="ml-7 pl-4 border-l-2 border-teal-100 space-y-2">
              <p className="text-sm font-medium text-gray-700">Who is required to set up 2FA?</p>
              {[
                {
                  value: 'optional',
                  label: 'Optional — staff can choose to enable 2FA for their own account',
                  description: 'Recommended for organisations starting out with 2FA.',
                },
                {
                  value: 'mandatory',
                  label: 'Mandatory — all staff must set up 2FA before accessing the portal',
                  description: 'Recommended for organisations with stricter security requirements.',
                },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:border-teal-300"
                  style={mfaMode === opt.value ? { borderColor: '#0d9488', background: '#f0fdfa' } : {}}>
                  <input type="radio" name="mfaMode" value={opt.value}
                    checked={mfaMode === opt.value}
                    onChange={() => setMfaMode(opt.value)}
                    className="mt-0.5 w-4 h-4 accent-teal-600 shrink-0" />
                  <div>
                    <p className="text-sm text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </Section>

        {/* 8 — Spend limits */}
        <Section
          id="policy"
          title="8. Travel Spend Limits"
          badge="optional"
          description="Set maximum accommodation rates and flight costs per destination. Used in the Travel Policy report to track compliance and, optionally, to trigger warnings or approvals when limits are exceeded."
        >
          <InfoBox>
            Leave this blank if you don't have formal spend policies yet — you can add them with STX later.
            Rates are in AUD, inclusive of GST.
          </InfoBox>

          {/* Accommodation */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">Accommodation — max per night by city</p>
            <p className="text-xs text-gray-600">e.g. Sydney $220, Melbourne $195, Brisbane $180</p>

            {accomRates.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr_130px_36px] bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">City</span>
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Max/night incl. GST</span>
                  <span />
                </div>
                {accomRates.map(({ city, rate }) => (
                  <div key={city} className="grid grid-cols-[1fr_130px_36px] px-3 py-2 items-center border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-700">{city}</span>
                    <span className="text-sm text-gray-700 text-right">${rate.toFixed(0)}</span>
                    <button type="button" onClick={() => removeAccomRate(city)}
                      className="text-gray-500 hover:text-red-400 text-base text-center">×</button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                <input className={inp + ' w-44'} value={newAccomCity} onChange={e => setNewAccomCity(e.target.value)}
                  placeholder="e.g. Sydney" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Max/night ($)</label>
                <input type="number" min="0" step="5" className={inp + ' w-28'} value={newAccomRate}
                  onChange={e => setNewAccomRate(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAccomRate())}
                  placeholder="e.g. 220" />
              </div>
              <button type="button" onClick={addAccomRate}
                disabled={!newAccomCity.trim() || !newAccomRate}
                className="px-3 py-2 bg-gray-700 text-white text-sm rounded-lg disabled:opacity-40 hover:bg-gray-800">
                + Add
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Flights — max total cost per trip by destination</p>
            <p className="text-xs text-gray-600">e.g. Sydney $350 return, Melbourne $280 return. This is the total flight cost for the trip, not per-leg.</p>

            {flightRates.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1fr_130px_36px] bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Destination</span>
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Max/trip incl. GST</span>
                  <span />
                </div>
                {flightRates.map(({ city, rate }) => (
                  <div key={city} className="grid grid-cols-[1fr_130px_36px] px-3 py-2 items-center border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-700">{city}</span>
                    <span className="text-sm text-gray-700 text-right">${rate.toFixed(0)}</span>
                    <button type="button" onClick={() => removeFlightRate(city)}
                      className="text-gray-500 hover:text-red-400 text-base text-center">×</button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Destination city</label>
                <input className={inp + ' w-44'} value={newFlightDest} onChange={e => setNewFlightDest(e.target.value)}
                  placeholder="e.g. Sydney" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Max/trip ($)</label>
                <input type="number" min="0" step="10" className={inp + ' w-28'} value={newFlightRate}
                  onChange={e => setNewFlightRate(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFlightRate())}
                  placeholder="e.g. 350" />
              </div>
              <button type="button" onClick={addFlightRate}
                disabled={!newFlightDest.trim() || !newFlightRate}
                className="px-3 py-2 bg-gray-700 text-white text-sm rounded-lg disabled:opacity-40 hover:bg-gray-800">
                + Add
              </button>
            </div>
          </div>
        </Section>

        {/* 9 — Policy compliance */}
        <Section
          id="variance"
          title="9. Policy Compliance Rules"
          badge="optional"
          description="When a booking exceeds your spend limit, you can choose whether the portal simply warns the staff member, or requires explicit approval before the trip can proceed."
        >
          <InfoBox>
            These rules only apply if you've set spend limits in section 7. You can configure a tolerance
            — e.g. allow up to 10% over the limit before triggering a response — so minor overruns don't
            create unnecessary friction. Leave this blank to discuss with STX.
          </InfoBox>

          {/* Accommodation variance */}
          <div className="space-y-3">
            <Toggle
              checked={variance.accommodation.enabled}
              onChange={v => setVar('accommodation', 'enabled', v)}
              label="Apply compliance rules to accommodation"
              description="When a nightly rate exceeds your accommodation limit, trigger a warning or approval."
            />
            {variance.accommodation.enabled && (
              <div className="ml-7 pl-4 border-l-2 border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">How much over the limit is acceptable before triggering a response?</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="number" min="0" step="1"
                      className={inp + ' w-24'}
                      value={variance.accommodation.value}
                      onChange={e => setVar('accommodation', 'value', parseFloat(e.target.value) || 0)}
                    />
                    <select
                      className={inp + ' w-40'}
                      value={variance.accommodation.type}
                      onChange={e => setVar('accommodation', 'type', e.target.value)}
                    >
                      <option value="percent">% over the limit</option>
                      <option value="amount">$ over the limit</option>
                    </select>
                    <p className="text-xs text-gray-600 w-full mt-1">
                      e.g. "10% over" means a $220 limit allows up to $242 before triggering.
                      Set to 0 to trigger on any amount over the limit.
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">When the threshold is exceeded, what should happen?</label>
                  <div className="space-y-2">
                    {[
                      { value: 'warn',    label: 'Show a warning — staff member is alerted but the booking can continue' },
                      { value: 'approve', label: 'Require approval — the trip must be approved before STX can proceed' },
                    ].map(opt => (
                      <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:border-teal-300"
                        style={variance.accommodation.action === opt.value ? { borderColor: '#0d9488', background: '#f0fdfa' } : {}}>
                        <input type="radio" name="accom-action" value={opt.value}
                          checked={variance.accommodation.action === opt.value}
                          onChange={() => setVar('accommodation', 'action', opt.value)}
                          className="mt-0.5 w-4 h-4 accent-teal-600 shrink-0" />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Flight variance */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <Toggle
              checked={variance.flight.enabled}
              onChange={v => setVar('flight', 'enabled', v)}
              label="Apply compliance rules to flights"
              description="When total flight costs exceed your flight limit, trigger a warning or approval."
            />
            {variance.flight.enabled && (
              <div className="ml-7 pl-4 border-l-2 border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">How much over the limit is acceptable before triggering a response?</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="number" min="0" step="1"
                      className={inp + ' w-24'}
                      value={variance.flight.value}
                      onChange={e => setVar('flight', 'value', parseFloat(e.target.value) || 0)}
                    />
                    <select
                      className={inp + ' w-40'}
                      value={variance.flight.type}
                      onChange={e => setVar('flight', 'type', e.target.value)}
                    >
                      <option value="percent">% over the limit</option>
                      <option value="amount">$ over the limit</option>
                    </select>
                    <p className="text-xs text-gray-600 w-full mt-1">
                      Set to 0 to trigger on any amount over the limit.
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">When the threshold is exceeded, what should happen?</label>
                  <div className="space-y-2">
                    {[
                      { value: 'warn',    label: 'Show a warning — staff member is alerted but the booking can continue' },
                      { value: 'approve', label: 'Require approval — the trip must be approved before STX can proceed' },
                    ].map(opt => (
                      <label key={opt.value} className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:border-teal-300"
                        style={variance.flight.action === opt.value ? { borderColor: '#0d9488', background: '#f0fdfa' } : {}}>
                        <input type="radio" name="flight-action" value={opt.value}
                          checked={variance.flight.action === opt.value}
                          onChange={() => setVar('flight', 'action', opt.value)}
                          className="mt-0.5 w-4 h-4 accent-teal-600 shrink-0" />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* 10 — Annual budget */}
        <Section
          id="budget"
          title="10. Annual Travel Budget"
          badge="optional"
          description="If your organisation tracks an annual travel budget, you can enter it here. The portal will show a live spend-vs-budget widget on the dashboard so you can monitor usage throughout the year."
        >
          <InfoBox>
            Budget figures are <strong>ex-GST</strong>. If you're not sure of your budget yet, leave this blank — it can be added or updated at any time through the portal.
          </InfoBox>

          <Toggle
            checked={budgetEnabled}
            onChange={setBudgetEnabled}
            label="I'd like to track an annual travel budget"
            description={budgetEnabled ? 'A budget vs actual widget will appear on the dashboard.' : 'No budget tracking — the dashboard will show spend figures only.'}
          />

          {budgetEnabled && (
            <div className="space-y-4 ml-7 pl-4 border-l-2 border-teal-100">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Financial year (ending year)
                  </label>
                  <input
                    type="number" min="2021" max="2041"
                    className={inp}
                    value={budgetFY}
                    onChange={e => setBudgetFY(parseInt(e.target.value) || budgetFY)}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    e.g. 2026 = Jul 2025–Jun 2026
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Alert at (% of budget used)
                  </label>
                  <input
                    type="number" min="0" max="100"
                    className={inp}
                    value={budgetThreshold}
                    onChange={e => setBudgetThreshold(parseInt(e.target.value) || 80)}
                    placeholder="80"
                  />
                  <p className="text-xs text-gray-600 mt-1">Dashboard highlights when this % is reached.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Overall annual budget ($ ex-GST) <span className="text-xs font-normal text-gray-600">(optional)</span>
                </label>
                <input
                  type="number" min="0" step="1000"
                  className={inp}
                  value={budgetOverall}
                  onChange={e => setBudgetOverall(e.target.value)}
                  placeholder="e.g. 100000"
                />
              </div>

              {costCentres.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Per cost centre budgets ($ ex-GST) <span className="text-xs font-normal text-gray-600">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Leave blank for any cost centres you don't want to track separately.
                  </p>
                  <div className="space-y-2">
                    {costCentres.map(cc => (
                      <div key={cc} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-44 shrink-0">{cc}</span>
                        <input
                          type="number" min="0" step="1000"
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-36 placeholder:text-gray-500"
                          value={budgetByCC[cc] ?? ''}
                          onChange={e => setBudgetByCC(prev => ({
                            ...prev,
                            [cc]: parseFloat(e.target.value) || 0,
                          }))}
                          placeholder="e.g. 25000"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* 11 — Tax settings */}
        <Section
          id="tax"
          title="11. Tax Settings"
          description="Set the GST rate applicable to your travel bookings."
        >
          <InfoBox>
            Most Australian organisations use 10% GST. If you're operating across New Zealand or are
            GST-exempt, select the appropriate option. This affects how costs are displayed and reported.
          </InfoBox>

          <div>
            <Label>GST rate</Label>
            <div className="space-y-2">
              {[
                { value: 0.10, label: '10% — Australian GST (most common)' },
                { value: 0.15, label: '15% — New Zealand GST' },
                { value: 0,    label: 'No GST / GST-exempt' },
              ].map(opt => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                  <input type="radio" name="gstRate" value={opt.value}
                    checked={gstRate === opt.value} onChange={() => setGstRate(opt.value)}
                    className="w-4 h-4 accent-teal-600" />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </Section>

        {/* 12 — Notes */}
        <Section
          id="notes"
          title="12. Questions & Notes"
          badge="optional"
          description="Use this space for any questions, special requirements, or anything you'd like to discuss with the STX team."
        >
          <textarea
            className={inp + ' min-h-[100px] resize-y'}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. We have specific NDIS plan management requirements, some staff require accessible accommodation only, we'd like to discuss our approval hierarchy..."
          />
        </Section>

        {/* Submit */}
        {error && <p className="text-sm text-red-600 text-center">{error}</p>}

        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center space-y-3">
          <p className="text-sm text-gray-700">
            When you're ready, submit your preferences below. You can always leave sections
            blank — STX will follow up to fill in any gaps.
          </p>
          <button type="submit" disabled={saving}
            className="px-8 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 disabled:opacity-50 text-sm">
            {saving ? 'Submitting…' : 'Submit my preferences'}
          </button>
        </div>

      </form>
    </Shell>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

function Shell({ clientName, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-teal-700 text-white px-6 py-5 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-200 mb-0.5">STX Corporate</p>
          <h1 className="text-xl font-bold">
            {clientName ? `${clientName} — Portal Setup` : 'Portal Onboarding'}
          </h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-8">
        {children}
      </main>
      <footer className="max-w-2xl mx-auto px-4 pb-8 text-center">
        <p className="text-xs text-gray-600">
          STX Corporate Travel Management · Questions? Contact <a href="mailto:enquiries@supportedtravelx.com.au" className="underline">enquiries@supportedtravelx.com.au</a>
        </p>
      </footer>
    </div>
  );
}
